import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";

const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const BORDER = "#D0D5DD";
const MUTED = "#98A2B3";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: PURPLE, borderWidth: 2 },
  },
  "& .MuiInputBase-input": {
    fontSize: 13,
    fontFamily: "Be Vietnam Pro",
    color: "#475467",
  },
  "& .MuiInputBase-input::placeholder": {
    color: MUTED,
    opacity: 1,
  },
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();
const uniqText = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => normalizeText(value))
        .filter(Boolean)
    )
  );

function Label({ children }) {
  return (
    <Typography data-popup-label
      sx={{
        mb: 0.75,
        fontSize: 13,
        fontWeight: 600,
        color: "#344054",
        fontFamily: "Be Vietnam Pro",
      }}
    >
      {children}
    </Typography>
  );
}

function normalizeDraft(values = {}) {
  return {
    fundingStage: normalizeText(values.fundingStage ?? values.funding_stage),
    lastFundingRoundAmount: normalizeText(
      values.lastFundingRoundAmount ?? values.last_funding_round_amount ?? values.lastRoundAmount
    ),
    totalRaisedToDate: normalizeText(
      values.totalRaisedToDate ?? values.total_raised_to_date ?? values.totalRaised
    ),
    revenueEstimate: normalizeText(values.revenueEstimate ?? values.revenue_estimate),
  };
}

export default function FundingFinancialsDialog({
  open,
  onClose,
  onSubmit,
  resetKey,
  summaryItems = [],
  initialValues = {},
  title = "Add Funding & Financials",
  sectionTitle = "Details",
  submitLabel = "Save Funding Details",
}) {
  const lastInitializedKeyRef = useRef("");
  const normalizedSummaryItems = useMemo(() => uniqText(summaryItems), [summaryItems]);
  const initialFundingStage = initialValues?.fundingStage;
  const initialFundingStageSnake = initialValues?.funding_stage;
  const initialLastFundingRoundAmount = initialValues?.lastFundingRoundAmount;
  const initialLastFundingRoundAmountSnake = initialValues?.last_funding_round_amount;
  const initialLastRoundAmount = initialValues?.lastRoundAmount;
  const initialTotalRaisedToDate = initialValues?.totalRaisedToDate;
  const initialTotalRaisedToDateSnake = initialValues?.total_raised_to_date;
  const initialTotalRaised = initialValues?.totalRaised;
  const initialRevenueEstimate = initialValues?.revenueEstimate;
  const initialRevenueEstimateSnake = initialValues?.revenue_estimate;
  const normalizedInitialDraft = useMemo(
    () =>
      normalizeDraft({
        fundingStage: initialFundingStage,
        funding_stage: initialFundingStageSnake,
        lastFundingRoundAmount: initialLastFundingRoundAmount,
        last_funding_round_amount: initialLastFundingRoundAmountSnake,
        lastRoundAmount: initialLastRoundAmount,
        totalRaisedToDate: initialTotalRaisedToDate,
        total_raised_to_date: initialTotalRaisedToDateSnake,
        totalRaised: initialTotalRaised,
        revenueEstimate: initialRevenueEstimate,
        revenue_estimate: initialRevenueEstimateSnake,
      }),
    [
      initialFundingStage,
      initialFundingStageSnake,
      initialLastFundingRoundAmount,
      initialLastFundingRoundAmountSnake,
      initialLastRoundAmount,
      initialTotalRaisedToDate,
      initialTotalRaisedToDateSnake,
      initialTotalRaised,
      initialRevenueEstimate,
      initialRevenueEstimateSnake,
    ]
  );
  const [draft, setDraft] = useState(() => normalizedInitialDraft);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    const nextInitializedKey = String(resetKey ?? "__default__");
    if (lastInitializedKeyRef.current === nextInitializedKey) return;

    lastInitializedKeyRef.current = nextInitializedKey;
    setDraft(normalizedInitialDraft);
    setSubmitError("");
  }, [normalizedInitialDraft, open, resetKey]);

  useEffect(() => {
    if (open) return;
    lastInitializedKeyRef.current = "";
    setSubmitting(false);
    setSubmitError("");
  }, [open]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const handleChange = useCallback((field) => {
    return (event) => {
      const nextValue = event?.target?.value ?? "";
      setDraft((prev) => ({
        ...prev,
        [field]: nextValue,
      }));
    };
  }, []);

  const handleSubmit = useCallback(async () => {
    const normalizedDraft = normalizeDraft(draft);
    const hasAnyValue = Object.values(normalizedDraft).some(Boolean);
    const hadInitialValue = Object.values(normalizedInitialDraft).some(Boolean);

    if (!hasAnyValue && !hadInitialValue) {
      setSubmitError("Enter at least one funding detail to continue.");
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError("");
      await onSubmit?.(normalizedDraft);
      onClose?.();
    } catch (error) {
      console.error("Failed to save funding and financial details:", error);
      setSubmitError(error?.message || "Failed to save funding details.");
    } finally {
      setSubmitting(false);
    }
  }, [draft, normalizedInitialDraft, onClose, onSubmit]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: "min(760px, calc(100vw - 32px))",
          maxWidth: "760px",
          borderRadius: "22px",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
          m: 2,
        },
      }}
    >
      <Box sx={{ px: { xs: 2.25, sm: 4 }, py: { xs: 2.25, sm: 3.25 } }}>
        <Stack data-popup-header direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.3}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "999px",
                display: "grid",
                placeItems: "center",
                color: "#3451F4",
                backgroundColor: "#F5F3FF",
              }}
            >
              <PaidOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography data-popup-title
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#101828",
                fontFamily: "Be Vietnam Pro",
              }}
            >
              {title}
            </Typography>
          </Stack>

          <IconButton data-popup-close
            onClick={handleClose}
            aria-label="Close"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "999px",
              backgroundColor: PURPLE,
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: PURPLE,
                opacity: 0.92,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {normalizedSummaryItems.length ? (
          <Box
            sx={{
              mt: 3.25,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: BLUE,
                fontFamily: "Be Vietnam Pro",
              }}
            >
              Info:
            </Typography>
            {normalizedSummaryItems.map((item, index) => (
              <React.Fragment key={`${item}-${index}`}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#475467",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {item}
                </Typography>
                {index < normalizedSummaryItems.length - 1 ? (
                  <Typography sx={{ fontSize: 13, color: "#D0D5DD" }}>|</Typography>
                ) : null}
              </React.Fragment>
            ))}
          </Box>
        ) : null}

        <Typography data-popup-section
          sx={{
            mt: 4.25,
            mb: 2,
            fontSize: 14,
            fontWeight: 700,
            color: BLUE,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {sectionTitle}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2.2,
          }}
        >
          <Box>
            <Label>Funding Stage</Label>
            <TextField
              fullWidth
              placeholder="Series A"
              value={draft.fundingStage}
              onChange={handleChange("fundingStage")}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Last Funding Round Amount</Label>
            <TextField
              fullWidth
              placeholder="$100,000"
              value={draft.lastFundingRoundAmount}
              onChange={handleChange("lastFundingRoundAmount")}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Total Raised To Date</Label>
            <TextField
              fullWidth
              placeholder="$250,000"
              value={draft.totalRaisedToDate}
              onChange={handleChange("totalRaisedToDate")}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Revenue Estimate</Label>
            <TextField
              fullWidth
              placeholder="$1,250,000"
              value={draft.revenueEstimate}
              onChange={handleChange("revenueEstimate")}
              sx={fieldSx}
            />
          </Box>
        </Box>

        {submitError ? (
          <Typography
            sx={{
              mt: 2.2,
              fontSize: 12.5,
              fontWeight: 500,
              color: "#F04438",
              fontFamily: "Be Vietnam Pro",
            }}
          >
            {submitError}
          </Typography>
        ) : null}

        <Box data-popup-footer-inline sx={{ display: "flex", justifyContent: "flex-end", mt: 4.5 }}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              minWidth: 164,
              height: 40,
              px: 2.5,
              borderRadius: "999px",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              border: `1px solid ${PURPLE}`,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#FFFFFF",
                color: PURPLE,
                borderColor: PURPLE,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                color: "#FFFFFF",
                backgroundColor: "#CBB0FF",
                borderColor: "#CBB0FF",
              },
            }}
          >
            {submitting ? "Saving..." : submitLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
